'use client';

import { ChevronDownIcon } from 'lucide-react';
import { useState } from 'react';

import { playSfx } from '@/hooks/use-sound';
import { cn } from '@/lib/utils';

/**
 * The seam between the two reading modes: the story ends here, and everything
 * below is proof for whoever wants to audit it. Collapsed by default so a
 * casual reader is finished at the story, and the dashboard never has to
 * carry the narrative.
 */
export function EvidenceDisclosure({
  label,
  hint,
  note,
  defaultOpen = false,
  children,
}: {
  label: string;
  hint: string;
  /** Optional clarifier rendered under the trigger while open. */
  note?: React.ReactNode;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div id="evidence" className="scroll-mt-20">
      <button
        type="button"
        aria-expanded={open}
        aria-controls="evidence-panel"
        data-sfx-managed=""
        onClick={() => {
          playSfx(open ? 'close' : 'open');
          setOpen(!open);
        }}
        className={cn(
          'group/ev screen-line-before screen-line-after flex w-full items-center gap-3 px-4 py-4 text-left',
          'transition-colors hover:bg-accent2'
        )}
      >
        <span
          aria-hidden
          className={cn(
            'flex size-6 shrink-0 items-center justify-center rounded-lg',
            'border border-muted-foreground/15 bg-muted text-muted-foreground',
            'ring-1 ring-edge ring-offset-1 ring-offset-background'
          )}
        >
          <ChevronDownIcon
            className={cn(
              'size-3.5 transition-transform duration-300',
              open && 'rotate-180'
            )}
          />
        </span>

        <span className="min-w-0 flex-1">
          <span className="block font-medium underline-offset-4 group-hover/ev:underline">
            {label}
          </span>
          <span className="block font-mono text-xs text-muted-foreground">
            {hint}
          </span>
        </span>
      </button>

      <div
        id="evidence-panel"
        className={cn(
          'grid transition-[grid-template-rows] duration-300 ease-out',
          open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        )}
      >
        {/* inert keeps the collapsed dashboard out of the a11y tree and tab order. */}
        {/* overflow-clip (not hidden): clip does not create a scroll container,
            so the explorer's sticky filter bar can still stick to the page. */}
        <div className="overflow-clip" inert={!open}>
          {note && (
            <p className="max-w-[68ch] px-4 pt-3 text-sm text-foreground/75">
              {note}
            </p>
          )}
          {children}
        </div>
      </div>
    </div>
  );
}
